import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { reminderPolicyKeys } from '@/lib/queryKeys';
import type { ReminderPolicy, ReminderType } from '@/types/reminder-policy';

/**
 * Resets a `ReminderPolicy` to spec defaults. Invalidates list + detail caches.
 *
 * Backend: `POST /v2/reminder-policies/:type/reset` →
 * `apps/operations/src/modules/reminders/policy/reminder-policy.controller.ts#reset`.
 */
export function useResetReminderPolicy() {
  const queryClient = useQueryClient();

  return useApiMutation<ReminderPolicy, { type: ReminderType }>({
    mutationFn: ({ type }) =>
      apiPost<ReminderPolicy, undefined>(
        operationsClient,
        endpoints.reminderPoliciesV2.reset(type),
        undefined,
      ),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(reminderPolicyKeys.detail(variables.type), data);
      void queryClient.invalidateQueries({ queryKey: reminderPolicyKeys.lists() });
      toast.success('Reminder policy reset to defaults');
    },
  });
}
